import React, { useState, useEffect } from 'react';
import { Download, FileSpreadsheet, Loader2, Plus, Settings } from 'lucide-react';
import { useProductStore } from '../../stores/productStore';
import { useFolderStore } from '../../stores/folderStore';
import { useAuthStore } from '../../stores/authStore';
import { createImageZip, downloadZip } from '../../utils/imageCompression';
import { downloadCSV } from '../../utils/csvExport';
import { ImportFromZip } from './ImportFromZip';　
import { WatermarkSettings } from './WatermarkSettings';
import { DownloadProgress } from './DownloadProgress';
import { hideGlobalLoading, showGlobalLoading } from '../../hooks/useLoading';

export function DownloadActions() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [showWatermarkSettings, setShowWatermarkSettings] = useState(false);
  
  // Load settings from localStorage with defaults
  const loadSetting = (key: string, defaultValue: any) => {
    try {
      const saved = localStorage.getItem(`imageSettings_${key}`);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };
  
  const [watermarkEnabled, setWatermarkEnabled] = useState(() => loadSetting('watermarkEnabled', true));
  const [watermarkSize, setWatermarkSize] = useState(() => loadSetting('watermarkSize', 50));
  const [compressionQuality, setCompressionQuality] = useState(() => loadSetting('compressionQuality', 20));
  const [supabaseQuality, setSupabaseQuality] = useState(() => loadSetting('supabaseQuality', 20));
  const [squareOutput, setSquareOutput] = useState(() => loadSetting('squareOutput', true));
  const [brightness, setBrightness] = useState(() => loadSetting('brightness', 100));
  
  // Save settings to localStorage
  const saveSetting = (key: string, value: any) => {
    try {
      localStorage.setItem(`imageSettings_${key}`, JSON.stringify(value));
    } catch (error) {
      console.error('Failed to save setting to localStorage:', error);
    }
  };
  
  const [currentImage, setCurrentImage] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const products = useProductStore(state => state.products);
  const loadProducts = useProductStore(state => state.loadProducts);
  const addProduct = useProductStore(state => state.addProduct);
  const currentFolderId = useFolderStore(state => state.currentFolderId);
  const user = useAuthStore(state => state.user);
  const canAddMore = true;

  const selectedProducts = products.filter(p => p.isSelected);
  const targetProducts = selectedProducts.length > 0 ? selectedProducts : products;

  const handleDownloadAll = async () => {
    if (isProcessing || targetProducts.length === 0) return;

    const allImages = targetProducts.flatMap(product => product.images);
    if (allImages.length === 0) return;

    setIsProcessing(true);
    setCurrentImage(0);
    setTotalImages(allImages.length);

    try {
      const zipBlob = await createImageZip(
        allImages,
        targetProducts,
        {
          watermark: watermarkEnabled,
          watermarkSize: watermarkSize,
          quality: compressionQuality / 100, // Convert percentage to decimal
          squareOutput: squareOutput, // Add square output option
          brightness: brightness / 100 // Convert percentage to decimal
        },
        (current) => setCurrentImage(current)
      );
      const timestamp = new Date().toISOString().split('T')[0];
      downloadZip(zipBlob, `yahoo_auction_${timestamp}.zip`);
    } catch (error) {
      console.error('Failed to create ZIP:', error);
    } finally {
      setIsProcessing(false);
      setCurrentImage(0);
      setTotalImages(0);
    }
  };

  const handleDownloadCSV = async () => {
    if (isDownloadingCSV) return;
    
    // Check if any products are selected
    if (selectedProducts.length === 0 && products.length > 0) {
      alert('CSVをダウンロードするには、少なくとも1つの商品を選択してください。');
      return;
    }
    
    setIsDownloadingCSV(true);
    try {
      // Use current products state (don't refresh to preserve selection state)
      if (targetProducts.length === 0) return;
      
      // Get user's description template setting
      const useDescriptionTemplate = user?.settings?.useDescriptionTemplate ?? true;
      
      downloadCSV(targetProducts, { encoding: 'utf8' }, useDescriptionTemplate);
    } catch (error) {
      console.error('Failed to refresh data before CSV export:', error);
      // If any error occurs, use current data as fallback
      if (targetProducts.length > 0) {
        const useDescriptionTemplate = user?.settings?.useDescriptionTemplate ?? true;
        downloadCSV(targetProducts, { encoding: 'utf8' }, useDescriptionTemplate);
      }
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
          <button
            onClick={handleDownloadCSV}
            disabled={products.length === 0 || isDownloadingCSV}
            className="flex items-center justify-center gap-2 px-4 py-3 h-18 rounded-md text-sm font-medium transition-colors w-full sm:w-auto text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloadingCSV ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-5 h-5" />
            )}
            <span>
              {isDownloadingCSV ? 'データを更新中...' : `CSVをダウンロード${selectedProducts.length > 0 ? ` (${selectedProducts.length}件)` : ''}`}
            </span>
          </button>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleDownloadAll}
              disabled={isProcessing || products.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-3 h-18 rounded-md text-sm font-medium transition-colors w-full sm:w-auto text-white bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span>画像とCSVをダウンロード{selectedProducts.length > 0 ? ` (${selectedProducts.length}件)` : ''}</span>
            </button>

            <button
              onClick={() => setShowWatermarkSettings(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>画像設定</span>
            </button>
          </div>

          <ImportFromZip />
        </div>

        {currentFolderId !== null && (
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">

            <div className="relative">
              <button
                onClick={async () => {
                  showGlobalLoading();
                  try {
                    await addProduct(false);
                  } catch (error) {
                    alert(error instanceof Error ? error.message : '商品の作成に失敗しました');
                  }
                  hideGlobalLoading();
                }}
                disabled={!canAddMore}
                className={`flex items-center justify-center gap-2 px-4 py-3 h-18 rounded-md text-sm font-medium text-white w-full sm:w-auto ${
                  canAddMore 
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                <Plus className="w-5 h-5" />
                <span>商品を追加</span>
              </button>
              {!canAddMore && (
                <div className="absolute top-full left-0 mt-1 text-xs text-red-600">
                  無料プランでは最大10商品まで
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showWatermarkSettings && (
        <WatermarkSettings
          enabled={watermarkEnabled}
          size={watermarkSize}
          quality={compressionQuality}
          squareOutput={squareOutput}
          brightness={brightness}
          supabaseQuality={supabaseQuality}
          onEnabledChange={(value) => {
            setWatermarkEnabled(value);
            saveSetting('watermarkEnabled', value);
          }}
          onSizeChange={(value) => {
            setWatermarkSize(value);
            saveSetting('watermarkSize', value);
          }}
          onQualityChange={(value) => {
            setCompressionQuality(value);
            saveSetting('compressionQuality', value);
          }}
          onSquareOutputChange={(value) => {
            setSquareOutput(value);
            saveSetting('squareOutput', value);
          }}
          onBrightnessChange={(value) => {
            setBrightness(value);
            saveSetting('brightness', value);
          }}
          onSupabaseQualityChange={(value) => {
            setSupabaseQuality(value);
            saveSetting('supabaseQuality', value);
          }}
          onClose={() => setShowWatermarkSettings(false)}
        />
      )}

      <DownloadProgress
        current={currentImage}
        total={totalImages}
        isVisible={isProcessing}
      />
    </div>
  );
}